<script lang="ts">
    import type * as t from "./types";

    import { fly } from "svelte/transition";
    import { cn } from "./utils";
    import ShopifyCdnImage from "~/components/ShopifyCdnImage.svelte";
    import { getAppContext } from "./quiz-store";
    import { EventEmitter } from "~/event-emitter";
    import PaddingX from "./components/PaddingX.svelte";

    export let visible;
    export let settings: t.EducationalStep;

    let videoSrc: t.Shopify.VideoSource | undefined;
    if (settings.video != null) {
        videoSrc = settings.video.sources?.find((source) => {
            return source.mime_type.startsWith("video");
        });
    }
    const hasMedia = settings.image != null || settings.video != null;
    const mediaTransitionDelay = settings.order === "media_text" ? 350 : 700;
    const textTransitionDelay = settings.order === "text_media" ? 350 : 700;
    const ctx = getAppContext();

    let container: HTMLDivElement;
    let introSemaphore = 0;

    function setupContainer() {
        ctx.events.emit<t.AppEvent.OnContentReady>(
            EventEmitter.EVENT.ON_CONTENT_READY,
            {
                target: container,
            },
        );
    }

    function registerIntro() {
        ++introSemaphore;
    }

    function handleIntroEnd() {
        --introSemaphore;
    }

    $: if (visible && introSemaphore === 0 && container != null) {
        setupContainer();
    }
</script>

{#if visible}
    <PaddingX class="size-full">
        <div
            class="flex size-full flex-col justify-center gap-4"
            bind:this={container}
        >
            <div
                class={cn(
                    "relative min-h-[50%]",
                    settings.order === "text_media" ? "order-2" : "order-1",
                    !hasMedia && "hidden",
                )}
                transition:fly={{ y: 48, delay: mediaTransitionDelay }}
                on:introend={handleIntroEnd}
                on:introstart={registerIntro}
            >
                {#if videoSrc != null}
                    {@const poster = settings.video?.preview_image}
                    <video
                        class="m-auto size-full rounded-lg object-cover sm:max-w-[75%]"
                        autoplay
                        controls
                        muted
                        preload="metadata"
                        width={videoSrc.width}
                        height={videoSrc.height}
                        poster={poster?.src}
                    >
                        <source src={videoSrc.url} type="video/mp4" />
                    </video>
                {:else if settings.image != null}
                    <ShopifyCdnImage
                        class="absolute inset-0 aspect-video size-full object-contain"
                        image={settings.image}
                        maxWidth={600}
                    />
                {/if}
            </div>
            <div
                class={cn(
                    "space-y-4 text-center",
                    hasMedia ? "text-xl" : "text-xl sm:text-3xl",
                    settings.order === "text_media" ? "order-1" : "order-2",
                )}
                transition:fly={{ y: 48, delay: textTransitionDelay }}
                on:introend={handleIntroEnd}
                on:introstart={registerIntro}
            >
                <div class="font-bold uppercase">
                    {@html settings.title}
                </div>
                {#if settings.text}
                    <div class="text-[0.75em] uppercase">
                        {@html settings.text}
                    </div>
                {/if}
            </div>
        </div>
    </PaddingX>
{/if}
